import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SnackBarContext } from "components/snack-bar";
import { CHROME_DOWNLOAD_URL, isSupportedBrowser } from "./detectBrowser";

/**
 * "Works best in Google Chrome" warning modal.
 *
 * Shown on every full page load of the flyer builder when the user is
 * on a browser other than Chrome or Firefox. They can either:
 *
 *   - "Continue on this browser" → modal closes, and a warning
 *     snackbar fires immediately as a softer reminder.
 *   - "Get Google Chrome" → opens the Chrome download page in a new
 *     tab.
 *
 * The X icon in the top-right corner and a backdrop click are both
 * treated as "Continue" (close + warning snackbar) — any dismissal
 * that isn't "Get Chrome" implies the user wants to stay where they
 * are, and we still want the softer reminder in the corner.
 *
 * Dismissal model
 * ---------------
 * We use a module-scope flag (resets when the JS bundle re-evaluates,
 * i.e. on F5 / Cmd+R / first navigation), NOT localStorage. The
 * effect:
 *
 *   - Real page reload → modal shows again (this is what the user
 *     asked for — they explicitly wanted the nag on every refresh).
 *   - SPA navigation away and back within the same tab session →
 *     React unmounts/remounts the component but the module-scope
 *     flag survives, so we don't re-open after the user already
 *     dismissed it in this load.
 *
 * Same pattern `_guestSessionInitialMountHandled` uses in
 * `PublicBuilder.js` for the guest-room cleanup latch.
 *
 * The component is self-contained: mount it once near the top of the
 * flyer builder tree and it handles its own detection and snackbar
 * wiring.
 */

// Survives React unmount/remount cycles (SPA nav inside the same
// page-load session) but RESETS on a real page reload because the
// bundle re-evaluates and this variable goes back to `false`.
let _dismissedThisLoad = false;

const FONT_STACK = '"Inter", "Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/** 24×24 close glyph used in the upper-right corner of the card. */
const CloseIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2E3860"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden
  >
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

export const BrowserWarningModal: React.FC = () => {
  const runSnackBar = useContext(SnackBarContext);
  const [isOpen, setIsOpen] = useState(false);

  // Decide on mount whether to show the modal. We only show if:
  //   1. The user is NOT on a supported browser.
  //   2. They haven't already dismissed it within this page-load
  //      session (module-scope flag — see banner above).
  useEffect(() => {
    if (isSupportedBrowser()) return;
    if (_dismissedThisLoad) return;
    setIsOpen(true);
  }, []);

  // Fire the orange warning snackbar. Reused by every dismissal path
  // that isn't "Get Chrome" — explicit Continue button, X icon,
  // backdrop click — so the user always gets the softer reminder
  // after waving the modal away.
  const fireWarningSnackbar = () => {
    runSnackBar({
      type: "warning",
      vertical: "top",
      horizontal: "center",
      msg: "Works best in Chrome",
    });
  };

  const handleContinue = () => {
    _dismissedThisLoad = true;
    setIsOpen(false);
    fireWarningSnackbar();
  };

  const handleGetChrome = () => {
    _dismissedThisLoad = true;
    setIsOpen(false);
    // `noopener` + `noreferrer` for tab isolation; opens in a new
    // tab so the user doesn't lose their in-progress design when
    // they navigate to install Chrome.
    if (typeof window !== "undefined") {
      window.open(CHROME_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      onClick={(e) => {
        // Same Continue-path on backdrop click — closes + warns.
        if (e.target === e.currentTarget) handleContinue();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0, 8, 30, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT_STACK,
      }}
    >
      {/* Card — geometry from the Figma spec (555×?, 24/24/32/24
          padding, gap 10 between the X row and the body block). */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          width: 555,
          maxWidth: "calc(100vw - 32px)",
          padding: "24px 24px 32px 24px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          borderRadius: 8,
          border: "1px solid #E2E2FC", // --light-blue
          background: "#FFF",
          boxShadow: "0 2px 16px 0 rgba(37, 59, 97, 0.10)", // hover card
          boxSizing: "border-box",
        }}
      >
        {/* Close-icon row — anchored to the upper-right corner of the
            card, full width so the X aligns to the far right via
            `align-items: flex-end` on the column. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          <button
            type="button"
            onClick={handleContinue}
            aria-label="Close"
            data-cypress-id="browser-warning-modal-close-button"
            style={{
              width: 24,
              height: 24,
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body block — [title + paragraph copy] stacked tightly,
            then a 24px gap, then the buttons row. We split the copy
            and the buttons into two sub-blocks so the title/paragraph
            pair can sit at the tighter 8px spacing while the buttons
            stay 24px below the paragraph (Figma spec). Width 522 =
            card 555 − 2*16 visual inset. */}
        <div
          style={{
            display: "flex",
            width: 522,
            maxWidth: "100%",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              width: "100%",
            }}
          >
            <h2
              style={{
                margin: 0,
                color: "#222336", // --Colors-Neutral-100
                textAlign: "center",
                fontFeatureSettings: '"liga" off, "clig" off',
                fontFamily: FONT_STACK,
                fontSize: 24,
                fontWeight: 700,
                lineHeight: "normal",
              }}
            >
              Works best in Google Chrome
            </h2>
            <p
              style={{
                margin: 0,
                color: "#222336",
                textAlign: "center",
                fontFeatureSettings: '"liga" off, "clig" off',
                fontFamily: FONT_STACK,
                fontSize: 16,
                fontWeight: 400,
                lineHeight: "22px",
              }}
            >
              This editor performs best in Chrome. You can continue here, but some features may be slower or behave
              unexpectedly.
            </p>
          </div>

          {/* Button row — Continue (secondary outline) on the left,
              Get Chrome (primary filled coral) on the right.
              `justify-content: space-between` + `width: 100%` so the
              two buttons hug the opposite edges of the 522px body
              block (matches the Figma layout). Both buttons use
              `box-sizing: border-box` so the spec's different
              paddings (Continue 8/24, Get Chrome 0/24) still
              produce identical 40px-tall hit targets — without
              border-box the Continue button's vertical padding
              would balloon it past min/max-height. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <button
              type="button"
              onClick={handleContinue}
              data-cypress-id="browser-warning-modal-continue-button"
              style={{
                display: "flex",
                minHeight: 40,
                maxHeight: 40,
                padding: "8px 24px",
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
                borderRadius: 8,
                border: "1px solid #1A2547", // --Base-Secondary-Default
                background: "#FFF", // --Colors-Neutral-White
                color: "#1A2547",
                textAlign: "center",
                fontFeatureSettings: '"liga" off, "clig" off',
                fontFamily: FONT_STACK,
                fontSize: 16,
                fontWeight: 700,
                lineHeight: "120%", // 19.2px
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              Continue on this browser
            </button>
            <button
              type="button"
              onClick={handleGetChrome}
              data-cypress-id="browser-warning-modal-get-chrome-button"
              style={{
                display: "flex",
                minHeight: 40,
                maxHeight: 40,
                padding: "0 24px",
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
                borderRadius: 8,
                border: "none",
                background: "#EE4360", // --Base-Primary-Default
                color: "#FFF", // --Colors-Neutral-White
                textAlign: "center",
                fontFeatureSettings: '"liga" off, "clig" off',
                fontFamily: FONT_STACK,
                fontSize: 16,
                fontWeight: 700,
                lineHeight: "120%",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              Get Google Chrome
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal to body so the modal sits above the entire builder UI
  // regardless of any stacking-context quirks in the flyer tree.
  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};

export default BrowserWarningModal;
